import { useMemo, useState } from "react";
import type { AdbApplication } from "../../core/adb/AdbApplications";
import type { AdbService } from "../../core/adb/AdbService";
import { operationLogger } from "../../core/operations/OperationLogger";
import "../../styles/adb-applications.css";

type ApplicationFilter = "all" | "enabled" | "system" | "user" | "disabled" | "uninstalled";
type ApplicationAction = "" | "enable" | "disable" | "uninstall" | "clear" | "save";

export function AdbApplicationsPanel({ service, connected, busy }: { service: AdbService; connected: boolean; busy: boolean }) {
  const [applications, setApplications] = useState<AdbApplication[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<ApplicationFilter>("all");
  const [search, setSearch] = useState("");
  const [action, setAction] = useState<ApplicationAction>("");
  const [loading, setLoading] = useState(false);
  const [operationBusy, setOperationBusy] = useState(false);
  const [error, setError] = useState("");

  const visibleApplications = useMemo(() => applications.filter((application) => {
    const matchesFilter = filter === "all" || filter === application.state || filter === application.scope;
    return matchesFilter && application.packageName.toLowerCase().includes(search.trim().toLowerCase());
  }), [applications, filter, search]);

  const loadApplications = async () => {
    setLoading(true);
    setError("");
    try {
      setApplications(await service.listApplications());
      setSelected(new Set());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  };

  const togglePackage = (packageName: string, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(packageName); else next.delete(packageName);
      return next;
    });
  };

  const toggleVisible = (checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      for (const application of visibleApplications) {
        if (checked) next.add(application.packageName); else next.delete(application.packageName);
      }
      return next;
    });
  };

  const saveSelection = () => {
    const payload = applications.filter((application) => selected.has(application.packageName));
    const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `adb-applications-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const applyAction = async () => {
    if (!action || !selected.size) return;
    if (action === "save") {
      saveSelection();
      setAction("");
      return;
    }
    const selectedApplications = applications.filter((application) => selected.has(application.packageName));
    const actionLabel = action === "enable" ? "bật" : action === "disable" ? "tắt" : action === "uninstall" ? "gỡ cài đặt cho user 0" : "xóa dữ liệu";
    if (!window.confirm(`Bạn có chắc muốn ${actionLabel} ${selectedApplications.length} ứng dụng đã chọn?\n\n${selectedApplications.slice(0, 12).map((application) => application.packageName).join("\n")}${selectedApplications.length > 12 ? "\n…" : ""}`)) return;

    setOperationBusy(true);
    setError("");
    const started = operationLogger.start("ADB", `package ${action}`, `Đang ${actionLabel} ứng dụng`);
    const failures: string[] = [];
    try {
      for (const application of selectedApplications) {
        try {
          if (action === "enable") await service.enableApplication(application.packageName);
          if (action === "disable") await service.disableApplication(application.packageName);
          if (action === "uninstall") await service.uninstallApplication(application.packageName);
          if (action === "clear") await service.clearApplicationData(application.packageName);
        } catch (cause) {
          failures.push(`${application.packageName}: ${cause instanceof Error ? cause.message : String(cause)}`);
        }
      }
      await loadApplications();
      if (failures.length) {
        const message = failures.join("\n");
        setError(message);
        operationLogger.failure("ADB", `package ${action}`, started, message);
      } else {
        operationLogger.success("ADB", `package ${action}`, started, "Hoàn tất");
      }
    } finally {
      setOperationBusy(false);
      setAction("");
    }
  };

  const allVisibleSelected = visibleApplications.length > 0 && visibleApplications.every((application) => selected.has(application.packageName));
  const disabled = !connected || busy || loading || operationBusy;

  return (
    <section className="panel adb-applications-panel">
      <div className="panel-heading">
        <div><p className="eyebrow">PACKAGE MANAGER</p><h2>Ứng dụng trên thiết bị</h2></div>
        <button className="secondary" disabled={disabled} onClick={() => void loadApplications()}>{loading ? "Đang tải..." : "Tải danh sách"}</button>
      </div>
      <p className="muted small">Đọc package bằng <code>pm list packages</code>. Uninstall chỉ gỡ cho <code>user 0</code>, không xóa trực tiếp APK hệ thống khỏi phân vùng system.</p>
      {error && <p className="error-message" role="alert">{error}</p>}
      {!applications.length ? <p className="muted">{connected ? "Bấm Tải danh sách để đọc ứng dụng." : "Hãy kết nối thiết bị ADB trước."}</p> : <>
        <div className="button-row">
          <label className="checkbox"><input type="checkbox" checked={allVisibleSelected} onChange={(event) => toggleVisible(event.target.checked)} /> Chọn tất cả đang hiển thị</label>
          <span className="muted small">{visibleApplications.length}/{applications.length} ứng dụng · đã chọn {selected.size}</span>
        </div>
        <div className="option-row app-filter-row">
          {(["all", "enabled", "system", "user", "disabled", "uninstalled"] as ApplicationFilter[]).map((value) => <label className="checkbox" key={value}><input type="radio" name="adb-app-filter" checked={filter === value} onChange={() => setFilter(value)} /> {filterLabel(value)}</label>)}
        </div>
        <div className="inline-form">
          <input className="text-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Tìm package..." aria-label="Tìm ứng dụng" />
          <select className="select-input" value={action} onChange={(event) => setAction(event.target.value as ApplicationAction)} disabled={!selected.size || busy || operationBusy} aria-label="Thao tác ứng dụng">
            <option value="">Chọn thao tác...</option><option value="enable">Enable</option><option value="disable">Disable</option><option value="uninstall">Uninstall (user 0)</option><option value="clear">Clear data</option><option value="save">Save selection</option>
          </select>
          <button className={action === "uninstall" || action === "clear" ? "danger-button" : "secondary"} disabled={!action || !selected.size || disabled} onClick={() => void applyAction()}>Áp dụng</button>
        </div>
        <div className="table-wrap adb-app-list">
          <table>
            <thead><tr><th aria-label="Chọn" /><th>Tên ứng dụng / package</th><th>Package</th><th>UID</th><th>Phạm vi</th><th>Trạng thái</th><th>APK path</th></tr></thead>
            <tbody>{visibleApplications.map((application) => <tr key={application.packageName}>
              <td><input type="checkbox" checked={selected.has(application.packageName)} onChange={(event) => togglePackage(application.packageName, event.target.checked)} aria-label={`Chọn ${application.packageName}`} /></td>
              <td><strong>{application.packageName}</strong></td><td><code>{application.packageName}</code></td><td>{application.uid ?? "—"}</td><td>{scopeLabel(application.scope)}</td><td>{stateLabel(application.state)}</td><td><code>{application.apkPath || "—"}</code></td>
            </tr>)}</tbody>
          </table>
          {!visibleApplications.length && <p className="muted small">Không có package phù hợp bộ lọc.</p>}
        </div>
      </>}
    </section>
  );
}

function filterLabel(value: ApplicationFilter): string {
  return value === "all" ? "Tất cả" : value === "enabled" ? "Enabled" : value === "system" ? "System" : value === "user" ? "User" : value === "disabled" ? "Disabled" : "Uninstalled";
}

function scopeLabel(value: AdbApplication["scope"]): string {
  return value === "system" ? "System" : value === "user" ? "User" : "Other";
}

function stateLabel(value: AdbApplication["state"]): string {
  return value === "enabled" ? "Enabled" : value === "disabled" ? "Disabled" : "Uninstalled";
}
